import {
	computed,
	Inject,
	Injectable,
	OnDestroy,
	Signal,
	signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';
import { WONDER_SCHEDULE_EARLY_ACCESS_LEAD_MINUTES } from 'src/lib/constants/wonder-schedule';
import { ClassroomStudentModel } from 'src/lib/services/api/classroom-manager/models/classroom-student.model';
import { MeetupManagementListItemModel } from 'src/lib/services/api/organizations/meetups/meetup-management.model';
import { StudentCurrentCourseModel } from 'src/lib/services/api/students/classes/current-courses/student-current-course.model';
import { StudentCourseActivityFolderModel } from 'src/lib/services/api/students/classes/student-course.model';
import {
	ACTIVE_STUDENT_SERVICE,
	IActiveStudentService,
} from 'src/lib/services/startup/active-student/active-student.types';
import { StudentClassroomStoreService } from 'src/lib/services/stores/students/classroom/student-classroom-store.service';
import { StudentCourseStoreService } from 'src/lib/services/stores/students/course/student-course-store.service';
import { StudentMeetupStoreService } from 'src/lib/services/stores/students/meetup/student-meetup-store.service';
import { CurrentTimeService } from 'src/lib/services/utility/current-time/current-time.service';
import {
	WONDER_DEFAULT_CHARACTER,
	type WonderCharacterKey,
	type WonderCharacterSubjectKey,
} from 'src/lib/utilities/wonder/wonder-character.utilities';
import 'temporal-polyfill/global';
import { getWonderMeetupOccurrenceKey } from './wonder-activity-meetup.utilities';
import {
	WonderActivityScheduleCourseActivities,
	WonderActivityScheduleWeek,
	WonderWeeklyProgressDay,
	type WonderActivityScheduleItem,
} from './wonder-activity.model';
import {
	buildActivityScheduleWeek,
	buildWonderPacedAssignmentDays,
	buildWonderWeeklyProgressDays,
	getNextWonderPacedActivityForDay,
	getWonderActivityDayInTimezone,
	getWonderActivityScheduleMonths,
	getWonderActivityScheduleTimezone,
	hasWonderWeeklyProgressSessionDay,
	isWonderActivityDateInClassroomSession,
	selectPrimaryWonderActivityScheduleItem,
	type WonderPacedAssignmentDay,
} from './wonder-activity.utilities';

export type WonderActivityStoreLoadingResource =
	| 'classroom'
	| 'courseActivities'
	| 'currentCourses'
	| 'meetups';

type WonderActivityStoreLoadingState = Record<
	WonderActivityStoreLoadingResource,
	boolean
>;

interface WonderAssignmentPacingCache {
	classroom: ClassroomStudentModel | null;
	courseActivities: WonderActivityScheduleCourseActivities[];
	pacedAssignmentDays: readonly WonderPacedAssignmentDay[];
	timezone: string;
}

@Injectable()
export class WonderActivityStoreService implements OnDestroy {
	private readonly unsubscribe$ = new Subject<void>();

	/* Resources */
	private readonly _classroomSubject =
		new BehaviorSubject<ClassroomStudentModel | null>(null);
	private readonly _courseActivitiesSubject = new BehaviorSubject<
		WonderActivityScheduleCourseActivities[]
	>([]);
	private readonly _currentCoursesSubject = new BehaviorSubject<
		StudentCurrentCourseModel[]
	>([]);
	private readonly _meetupsSubject = new BehaviorSubject<
		MeetupManagementListItemModel[]
	>([]);

	private readonly _classroom = signal<ClassroomStudentModel | null>(null);
	private readonly _courseActivities = signal<
		WonderActivityScheduleCourseActivities[]
	>([]);

	/* Processed Items */
	private readonly _processedActivityItemsSubject =
		new BehaviorSubject<WonderActivityScheduleWeek>(
			buildActivityScheduleWeek(
				this.currentTime.now(),
				null,
				[],
				[],
				[],
				this.currentTime.currentTime,
			),
		);
	private readonly _processedNextWeekActivityItemsSubject =
		new BehaviorSubject<WonderActivityScheduleWeek>(
			buildActivityScheduleWeek(
				this.getNextWeekReference(this.currentTime.now(), null),
				null,
				[],
				[],
				[],
				this.currentTime.currentTime,
			),
		);

	/* Utilities */
	private readonly _loadingState = signal<WonderActivityStoreLoadingState>({
		classroom: false,
		courseActivities: false,
		currentCourses: false,
		meetups: false,
	});
	private readonly _currentDay$ = toObservable(this.currentTime.currentDay);
	private readonly _weeklyProgressDataRevision = signal(0);
	private assignmentPacingCache: WonderAssignmentPacingCache | null = null;

	public readonly hasError = signal(false);
	public readonly isLoading = computed(() =>
		Object.values(this._loadingState()).some(Boolean),
	);
	public readonly studentUid = signal<number | null>(null);
	public readonly classroom = computed(() => this._classroom());
	public readonly courseActivities = computed(() => this._courseActivities());
	public readonly isCurrentDayInSession = computed<boolean | null>(() =>
		this.isDateInSession(this.currentTime.currentTime()),
	);
	public readonly currentCourses = computed(() =>
		this._currentCoursesSubject.getValue(),
	);
	public readonly meetups = computed(() => this._meetupsSubject.getValue());

	public readonly communityFileHref = computed(() => {
		const communityFile = this._classroom()?.program?.community_file;
		const expirationDate = communityFile?.expiration_date;

		if (
			expirationDate != null &&
			expirationDate.getTime() <
				this.currentTime.currentTime().epochMilliseconds
		) {
			return null;
		}

		const communityFileUrl = communityFile?.url;

		return typeof communityFileUrl === 'string'
			? communityFileUrl.trim() || null
			: null;
	});

	constructor(
		@Inject(ACTIVE_STUDENT_SERVICE)
		private readonly activeStudentService: IActiveStudentService,
		private readonly currentTime: CurrentTimeService,
		private readonly studentClassroomStore: StudentClassroomStoreService,
		private readonly studentCourseStore: StudentCourseStoreService,
		private readonly studentMeetupStore: StudentMeetupStoreService,
	) {
		this.initClassroomSubject();
		this.initCurrentCoursesSubject();
		this.initCourseActivitiesSubject();
		this.initMeetupsSubject();
		this.initActivityScheduleWeekSubject();
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	/******
	 * Public Interface
	 *******/
	public isDateInSession(dateTime: Temporal.ZonedDateTime): boolean | null {
		const classroom = this._classroom();

		return classroom == null
			? null
			: isWonderActivityDateInClassroomSession(classroom, dateTime);
	}

	public buildWeeklyProgressDays(
		weekStart: Temporal.ZonedDateTime,
	): WonderWeeklyProgressDay[] {
		this._weeklyProgressDataRevision();
		const classroom = this._classroom();
		const courseActivities = this._courseActivities();
		const timezone = getWonderActivityScheduleTimezone(weekStart, classroom);

		return buildWonderWeeklyProgressDays(
			this.getPacedAssignmentDays(classroom, courseActivities, timezone),
			classroom,
			weekStart,
		);
	}

	public hasWeeklyProgressSessionDay(
		weekStart: Temporal.ZonedDateTime,
	): boolean {
		this._weeklyProgressDataRevision();

		return hasWonderWeeklyProgressSessionDay(this._classroom(), weekStart);
	}

	public getNextPacedActivityForDay(
		registrationId: number,
		activityId: string,
	): StudentCourseActivityFolderModel | null {
		const classroom = this._classroom();
		const courseActivities = this._courseActivities();
		const timezone = getWonderActivityScheduleTimezone(
			this.currentTime.currentDay(),
			classroom,
		);

		return getNextWonderPacedActivityForDay(
			classroom,
			courseActivities,
			timezone,
			registrationId,
			activityId,
			this.getPacedAssignmentDays(classroom, courseActivities, timezone),
		);
	}

	/**
	 * Every course's paced coursework, for the admin debug view that stands in
	 * until the API owns this calculation.
	 */
	public getPacedAssignmentSchedule(): readonly WonderPacedAssignmentDay[] {
		const classroom = this._classroom();
		const courseActivities = this._courseActivities();

		return this.getPacedAssignmentDays(
			classroom,
			courseActivities,
			getWonderActivityScheduleTimezone(
				this.currentTime.currentDay(),
				classroom,
			),
		);
	}

	public todaysSchedule$(): Observable<WonderActivityScheduleItem[]> {
		return combineLatest([
			this._processedActivityItemsSubject,
			this._currentDay$,
			this._classroomSubject,
		]).pipe(
			map(([scheduleWeek, today, classroom]) => {
				const timezone = getWonderActivityScheduleTimezone(today, classroom);

				return scheduleWeek[getWonderActivityDayInTimezone(today, timezone)];
			}),
		);
	}

	public readonly todaysSchedule: Signal<WonderActivityScheduleItem[]> =
		toSignal(this.todaysSchedule$(), { initialValue: [] });

	public weeksSchedule$(): Observable<WonderActivityScheduleWeek> {
		return this._processedActivityItemsSubject.asObservable();
	}

	public readonly weeksSchedule: Signal<WonderActivityScheduleWeek> = toSignal(
		this.weeksSchedule$(),
		{
			initialValue: buildActivityScheduleWeek(
				this.currentTime.now(),
				null,
				[],
				[],
				[],
				this.currentTime.currentTime,
			),
		},
	);
	public readonly nextWeeksSchedule: Signal<WonderActivityScheduleWeek> =
		toSignal(this._processedNextWeekActivityItemsSubject.asObservable(), {
			initialValue: buildActivityScheduleWeek(
				this.getNextWeekReference(this.currentTime.now(), null),
				null,
				[],
				[],
				[],
				this.currentTime.currentTime,
			),
		});

	public readonly currentActivity = computed(() =>
		selectPrimaryWonderActivityScheduleItem(
			this.todaysSchedule().filter((item) => item.moment.isCurrent()),
		),
	);
	public readonly displayActivity = computed(() => {
		const schedule = this.todaysSchedule();
		const currentTime = this.currentTime.currentTime();
		const displayThrough = currentTime.add({
			minutes: WONDER_SCHEDULE_EARLY_ACCESS_LEAD_MINUTES,
		});
		const upcomingStartDates = [
			...new Map(
				schedule
					.filter(
						(item) =>
							item.type !== 'noSchool' &&
							item.type !== 'skillBuilders' &&
							Temporal.ZonedDateTime.compare(item.startDate, currentTime) > 0 &&
							Temporal.ZonedDateTime.compare(item.startDate, displayThrough) <=
								0,
					)
					.map(
						(item) =>
							[
								item.startDate.toInstant().epochNanoseconds,
								item.startDate,
							] as const,
					),
			).values(),
		].sort((left, right) => Temporal.ZonedDateTime.compare(right, left));

		// Shift only real start transitions so fallback and end-driven handoffs
		// remain tied to the exact clock.
		for (const startDate of upcomingStartDates) {
			const primaryActivity = selectPrimaryWonderActivityScheduleItem(
				schedule.filter(
					(item) =>
						item.type !== 'noSchool' &&
						item.type !== 'skillBuilders' &&
						Temporal.ZonedDateTime.compare(item.startDate, startDate) <= 0 &&
						Temporal.ZonedDateTime.compare(startDate, item.endDate) < 0,
				),
			);

			if (
				primaryActivity != null &&
				Temporal.ZonedDateTime.compare(primaryActivity.startDate, startDate) ===
					0
			) {
				return primaryActivity;
			}
		}

		return this.currentActivity();
	});

	public readonly currentResourceLink = computed(
		() => this.currentActivity()?.resourceLink ?? null,
	);
	public readonly currentCharacter = computed<WonderCharacterKey>(
		() => this.currentActivity()?.getCharacter() ?? WONDER_DEFAULT_CHARACTER,
	);
	public readonly currentQuoteSubject =
		computed<WonderCharacterSubjectKey | null>(
			() => this.currentActivity()?.getSubjectKey() ?? null,
		);

	/******
	 * Init Functions
	 *******/
	private getActiveStudentUid$(): Observable<number> {
		return this.activeStudentService.getActiveStudent$().pipe(
			filter((studentUid): studentUid is number => studentUid != null),
			distinctUntilChanged(),
			tap((studentUid) => {
				this.studentUid.set(studentUid);
				this.hasError.set(false);
			}),
		);
	}

	private initClassroomSubject(): void {
		this.getActiveStudentUid$()
			.pipe(
				tap(() => {
					this.invalidateAssignmentPacingCache();
					this._classroomSubject.next(null);
					this._classroom.set(null);
					this.setResourceLoading('classroom', true);
				}),
				switchMap((studentUid) => {
					return this.studentClassroomStore.classroom$(studentUid).pipe(
						catchError(() => {
							this.hasError.set(true);
							return of(null);
						}),
					);
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe((classroom) => {
				this.invalidateAssignmentPacingCache();
				this._classroomSubject.next(classroom);
				this._classroom.set(classroom);
				this.setResourceLoading('classroom', false);
			});
	}

	private initCurrentCoursesSubject(): void {
		this.getActiveStudentUid$()
			.pipe(
				tap(() => {
					this._currentCoursesSubject.next([]);
					this.setResourceLoading('currentCourses', true);
				}),
				switchMap((studentUid) => {
					return this.studentCourseStore.current$(studentUid).pipe(
						catchError(() => {
							this.hasError.set(true);
							return of([]);
						}),
					);
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe((currentCourses) => {
				this._currentCoursesSubject.next(currentCourses ?? []);
				this.setResourceLoading('currentCourses', false);
			});
	}

	private initCourseActivitiesSubject(): void {
		combineLatest([this.getActiveStudentUid$(), this._currentCoursesSubject])
			.pipe(
				tap(() => {
					this.invalidateAssignmentPacingCache();
					this._courseActivitiesSubject.next([]);
					this._courseActivities.set([]);
				}),
				switchMap(([studentUid, currentCourses]) => {
					const coursesWithRegistration = currentCourses.filter(
						(course) => course.registration_id != null,
					);

					if (coursesWithRegistration.length === 0) {
						this.setResourceLoading('courseActivities', false);
						return of([] as WonderActivityScheduleCourseActivities[]);
					}

					this.setResourceLoading('courseActivities', true);
					return combineLatest(
						coursesWithRegistration.map((course) =>
							this.studentCourseStore
								.classActivities$(studentUid, course.registration_id)
								.pipe(
									map(
										(activities) =>
											({
												activities,
												course,
											}) as WonderActivityScheduleCourseActivities,
									),
									catchError(() => {
										this.hasError.set(true);
										return of({
											activities: null,
											course,
										} as WonderActivityScheduleCourseActivities);
									}),
								),
						),
					);
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe(
				(courseActivities: WonderActivityScheduleCourseActivities[]) => {
					this.invalidateAssignmentPacingCache();
					this._courseActivitiesSubject.next(courseActivities);
					this._courseActivities.set(courseActivities);
					this.setResourceLoading('courseActivities', false);
				},
			);
	}

	private initMeetupsSubject(): void {
		combineLatest([
			this.getActiveStudentUid$(),
			this._currentDay$,
			this._classroomSubject,
		])
			.pipe(
				tap(() => {
					this._meetupsSubject.next([]);
					this.setResourceLoading('meetups', true);
				}),
				switchMap(([studentUid, today, classroom]) => {
					const nextWeekReference = this.getNextWeekReference(today, classroom);
					const scheduleMonths = [
						...new Map(
							[
								...getWonderActivityScheduleMonths(today, classroom),
								...getWonderActivityScheduleMonths(
									nextWeekReference,
									classroom,
								),
							].map((scheduleMonth) => [
								`${scheduleMonth.year}:${scheduleMonth.month}`,
								scheduleMonth,
							]),
						).values(),
					];

					return combineLatest(
						scheduleMonths.map((scheduleMonth) =>
							this.studentMeetupStore
								.meetups$(
									studentUid,
									scheduleMonth.month - 1,
									scheduleMonth.year,
								)
								.pipe(
									catchError(() => {
										this.hasError.set(true);
										return of([]);
									}),
								),
						),
					).pipe(map((meetupLists) => this.dedupeMeetups(meetupLists.flat())));
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe((meetups) => {
				this._meetupsSubject.next(meetups ?? []);
				this.setResourceLoading('meetups', false);
			});
	}

	private initActivityScheduleWeekSubject(): void {
		combineLatest([
			this._currentDay$,
			this._classroomSubject,
			this._currentCoursesSubject,
			this._courseActivitiesSubject,
			this._meetupsSubject,
		])
			.pipe(
				map(([today, classroom, currentCourses, courseActivities, meetups]) => {
					const nextWeekReference = this.getNextWeekReference(today, classroom);
					const timezone = getWonderActivityScheduleTimezone(today, classroom);
					const pacedAssignmentDays = this.getPacedAssignmentDays(
						classroom,
						courseActivities,
						timezone,
					);
					const activityScheduleWeek = buildActivityScheduleWeek(
						today,
						classroom,
						currentCourses,
						courseActivities,
						meetups,
						this.currentTime.currentTime,
						pacedAssignmentDays,
					);
					const nextActivityScheduleWeek = buildActivityScheduleWeek(
						nextWeekReference,
						classroom,
						currentCourses,
						courseActivities,
						meetups,
						this.currentTime.currentTime,
						pacedAssignmentDays,
					);

					return {
						activityScheduleWeek,
						classroom,
						courseActivities,
						nextActivityScheduleWeek,
						today,
					};
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe(({ activityScheduleWeek, nextActivityScheduleWeek }) => {
				this._processedActivityItemsSubject.next(activityScheduleWeek);
				this._processedNextWeekActivityItemsSubject.next(
					nextActivityScheduleWeek,
				);
			});
	}

	/******
	 * Helper Functions
	 *******/
	private getPacedAssignmentDays(
		classroom: ClassroomStudentModel | null,
		courseActivities: WonderActivityScheduleCourseActivities[],
		timezone: string,
	): readonly WonderPacedAssignmentDay[] {
		const cached = this.assignmentPacingCache;

		if (
			cached?.classroom === classroom &&
			cached.courseActivities === courseActivities &&
			cached.timezone === timezone
		) {
			return cached.pacedAssignmentDays;
		}

		const pacedAssignmentDays = this.buildPacedAssignmentDays(
			classroom,
			courseActivities,
			timezone,
		);
		this.assignmentPacingCache = {
			classroom,
			courseActivities,
			pacedAssignmentDays,
			timezone,
		};

		return pacedAssignmentDays;
	}

	private buildPacedAssignmentDays(
		classroom: ClassroomStudentModel | null,
		courseActivities: WonderActivityScheduleCourseActivities[],
		timezone: string,
	): readonly WonderPacedAssignmentDay[] {
		return buildWonderPacedAssignmentDays(
			classroom,
			courseActivities,
			timezone,
		);
	}

	private invalidateAssignmentPacingCache(): void {
		this.assignmentPacingCache = null;
		this._weeklyProgressDataRevision.update((revision) => revision + 1);
	}

	private setResourceLoading(
		resource: WonderActivityStoreLoadingResource,
		isLoading: boolean,
	): void {
		const loadingState = this._loadingState();

		if (loadingState[resource] === isLoading) {
			return;
		}

		this._loadingState.set({
			...loadingState,
			[resource]: isLoading,
		});
	}

	private dedupeMeetups(
		meetups: MeetupManagementListItemModel[],
	): MeetupManagementListItemModel[] {
		const meetupByKey = new Map<string, MeetupManagementListItemModel>();

		for (const meetup of meetups) {
			const key = getWonderMeetupOccurrenceKey(meetup);

			if (!meetupByKey.has(key)) {
				meetupByKey.set(key, meetup);
			}
		}

		return [...meetupByKey.values()];
	}

	private getNextWeekReference(
		today: Temporal.ZonedDateTime,
		classroom: ClassroomStudentModel | null,
	): Temporal.ZonedDateTime {
		return today
			.withTimeZone(getWonderActivityScheduleTimezone(today, classroom))
			.add({ days: 7 });
	}
}
